/**
 * Central tuning constants for the whole game.
 * Everything is expressed in "logical pixels" on a fixed 448x640 playfield.
 * The renderer scales that field to whatever space the browser gives us.
 */

export const VIEW = Object.freeze({ W: 448, H: 640 });

/** Thickness of the arcade cabinet border drawn inside the canvas. */
export const BORDER = Object.freeze({ side: 16, top: 24 });

export const FIELD = Object.freeze({
  left: BORDER.side,
  right: VIEW.W - BORDER.side,
  top: BORDER.top,
  bottom: VIEW.H,
  width: VIEW.W - BORDER.side * 2,
});

/** Brick grid. 13 columns of 32px exactly fills the 416px playfield. */
export const GRID = Object.freeze({
  cols: 13,
  maxRows: 18,
  cellW: 32,
  cellH: 16,
  originX: FIELD.left,
  originY: 96,
});

export const PADDLE = Object.freeze({
  top: 592,
  height: 14,
  widths: { narrow: 44, normal: 68, wide: 104 },
  /** Keyboard travel speed in px/sec. */
  keySpeed: 540,
  /** How fast the paddle morphs between width states. */
  morphSpeed: 180,
  /** Fraction of paddle horizontal velocity added to ball spin. */
  spinTransfer: 0.16,
});

export const BALL = Object.freeze({
  radius: 5,
  baseSpeed: 296,
  maxSpeed: 640,
  /** Extra starting speed per round reached. */
  speedPerRound: 9,
  /** Paddle hits required before the next acceleration tick. */
  hitsPerRamp: 9,
  rampFactor: 1.032,
  /** Slow ramp applied while a round drags on, per second. */
  timeRamp: 1.6,
  /** Widest deflection from vertical when striking the paddle edge. */
  maxDeflect: 1.13,
  /** Smallest paddle strike offset, so the ball never leaves perfectly vertical. */
  minOffset: 0.06,
  /** Seconds without hitting a brick before the ball gets nudged off course. */
  stallTimeout: 11,
  /** Ball never travels flatter than this (keeps it from stalling). */
  minVerticalRatio: 0.26,
  trailLength: 9,
});

export const CAPSULE = Object.freeze({
  w: 30,
  h: 14,
  fallSpeed: 132,
  /** Drop chance per destroyed brick while no capsule is in play. */
  dropChance: 0.22,
  /** Bricks that must break before the first capsule of a round can drop. */
  warmup: 3,
});

export const LASER = Object.freeze({
  w: 3,
  h: 12,
  speed: 660,
  cooldown: 0.19,
});

export const RULES = Object.freeze({
  startingLives: 3,
  maxLives: 6,
  maxBalls: 6,
  /** Score thresholds that award an extra Vaus. */
  firstExtraLife: 20000,
  extraLifeInterval: 60000,
  /** Seconds a caught ball waits before auto-launching. */
  catchTimeout: 3.2,
  /** Seconds the "ROUND N" banner holds before play begins. */
  readyDuration: 1.9,
  clearDuration: 2.4,
  warpDuration: 1.5,
});

export const DOH = Object.freeze({
  w: 96,
  h: 112,
  hp: 22,
  /** Invulnerability window after taking a hit, in seconds. */
  iframe: 0.22,
  driftSpeed: 46,
  shotSpeed: 190,
  shotInterval: 2.1,
  shotSize: 10,
});

/** Points awarded per brick colour code. */
export const BRICK_SCORE = Object.freeze({
  w: 50, o: 60, c: 70, g: 80, r: 90, b: 100, m: 110, y: 120,
  S: 0, G: 0,
});

export const SCORE = Object.freeze({
  capsule: 1000,
  roundClear: 2500,
  warpBonus: 4000,
  dohDefeated: 30000,
  /** Silver bricks are worth 50 x round number. */
  silverPerRound: 50,
});
